import { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Search, ShieldAlert, ShieldCheck, Clock, Users, Flag, AlertTriangle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const riskBadge = {
  clear: { label: "Sin coincidencias", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", icon: ShieldCheck },
  medium: { label: "Revision manual", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20", icon: AlertTriangle },
  high: { label: "EDD requerido", cls: "bg-orange-500/10 text-orange-400 border-orange-500/20", icon: ShieldAlert },
  critical: { label: "Bloquear y revisar", cls: "bg-red-500/10 text-red-400 border-red-500/20", icon: ShieldAlert },
};

export default function Screening() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [showFlagDialog, setShowFlagDialog] = useState(false);

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/screening/history`, { withCredentials: true });
      setHistory(res.data.items || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const runBatchScreening = async () => {
    setBatchRunning(true);
    try {
      const res = await axios.post(`${API}/screening/batch`, {}, { withCredentials: true });
      setBatchResult(res.data);
      toast.success(`Screening completo: ${res.data.total_screened} personas analizadas`);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en screening batch");
    } finally {
      setBatchRunning(false);
    }
  };

  return (
    <Layout>
      <div data-testid="screening-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Screening PEP/OFAC</h1>
            <p className="text-slate-400 mt-1 text-sm">Verificacion contra listas de sanciones y PEP</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowFlagDialog(true)} variant="outline"
              className="border-slate-700 text-slate-300 hover:text-white" data-testid="add-flag-btn">
              <Flag size={16} className="mr-2" /> Agregar Bandera
            </Button>
            <Button onClick={runBatchScreening} disabled={batchRunning}
              className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="batch-screening-btn">
              <Users size={16} className="mr-2" />
              {batchRunning ? "Procesando..." : "Screening Masivo"}
            </Button>
          </div>
        </div>

        {/* Batch Result */}
        {batchResult && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-6" data-testid="batch-result">
            <h3 className="text-sm font-semibold text-white mb-3">Resultado del Screening Masivo</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-500">Total Analizados</p>
                <p className="text-xl font-bold text-white">{batchResult.total_screened}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Coincidencias</p>
                <p className="text-xl font-bold text-red-400">{batchResult.matches_found}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Sin Coincidencias</p>
                <p className="text-xl font-bold text-emerald-400">{batchResult.clear}</p>
              </div>
            </div>
          </div>
        )}

        {/* History Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl" data-testid="screening-history">
          <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Historial de Screening</h2>
            <span className="text-xs text-slate-500">{history.length} registros</span>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">No hay registros de screening</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800 text-left">
                    <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Persona</th>
                    <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Tipo</th>
                    <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Resultado</th>
                    <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Coincidencias</th>
                    <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Listas</th>
                    <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {history.map((s) => {
                    const badge = riskBadge[s.risk_classification] || riskBadge.clear;
                    const BadgeIcon = badge.icon;
                    return (
                      <tr key={s.screening_id} className="hover:bg-slate-800/50">
                        <td className="px-5 py-2.5 text-sm text-white">{s.person_name}</td>
                        <td className="px-5 py-2.5 text-sm text-slate-400">{s.person_type}</td>
                        <td className="px-5 py-2.5">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${badge.cls}`}>
                            <BadgeIcon size={12} /> {badge.label}
                          </span>
                        </td>
                        <td className="px-5 py-2.5 text-sm font-mono text-slate-300">{s.total_matches}</td>
                        <td className="px-5 py-2.5 text-xs text-slate-500">
                          {(s.lists_screened || []).join(", ")}
                        </td>
                        <td className="px-5 py-2.5 text-xs text-slate-500">
                          {s.screened_at ? new Date(s.screened_at).toLocaleString("es-MX") : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <FlagDialog open={showFlagDialog} onClose={() => setShowFlagDialog(false)} onAdded={loadHistory} />
      </div>
    </Layout>
  );
}

function FlagDialog({ open, onClose, onAdded }) {
  const [name, setName] = useState("");
  const [rfc, setRfc] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name) return toast.error("Nombre es requerido");
    setSaving(true);
    try {
      await axios.post(`${API}/screening/flag`, { name, rfc, reason }, { withCredentials: true });
      toast.success("Bandera agregada al sistema interno");
      setName(""); setRfc(""); setReason("");
      onClose();
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al agregar bandera");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="flag-dialog">
        <DialogHeader>
          <DialogTitle>Agregar Bandera Interna</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Nombre completo</Label>
            <Input className="bg-slate-800 border-slate-700" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Nombre completo" data-testid="flag-name-input" />
          </div>
          <div>
            <Label>RFC</Label>
            <Input className="bg-slate-800 border-slate-700" value={rfc} onChange={(e) => setRfc(e.target.value)}
              placeholder="RFC (opcional)" data-testid="flag-rfc-input" />
          </div>
          <div>
            <Label>Razon</Label>
            <Input className="bg-slate-800 border-slate-700" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo de la bandera" data-testid="flag-reason-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="flag-save-btn">
            {saving ? "Guardando..." : "Agregar Bandera"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
